import { useCallback, useMemo, useState, type ComponentProps, type ReactNode } from "react";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
  Box,
  Button,
  Field,
  FieldLabel,
  Icon,
  SelectCombobox,
  SelectComboboxPopup,
  SelectComboboxButton,
  SelectEmpty,
  SelectGroup,
  SelectGroupLabel,
  SelectItem,
  SelectSeparator,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  useSelectQuery,
  type IconName,
  type SelectComboboxOption,
} from "@salestrak/ui";

import type { CustomFieldDef } from "#app/components/custom-fields/types";
import { formPropertyIcon } from "#app/components/forms/form-builder/form-item-identity";
import { getFieldTypeMeta } from "#app/lib/constants/field-type-icons";
import type { CustomFieldType } from "#app/lib/data";
import { useAllCustomFields } from "#app/lib/hooks/use-all-custom-fields";
import { useIssueSourceSettings } from "#app/lib/hooks/use-issue-source-settings";
import {
  FORM_PROPERTIES,
  FORM_PROPERTY_FIELD_TYPE,
  FORM_PROPERTY_LABEL,
  linkItemToField,
  linkItemToProperty,
  propertyFieldId,
  questionKindForFieldType,
  resolveItemField,
  unlinkItem,
  type Form,
  type FormFieldItem,
  type FormItem,
  type FormProperty,
} from "#app/lib/forms";

export type ItemRelink = (item: FormFieldItem) => FormItem;

// Sentinel values no field id or `property:<key>` collides with; the new-field door is in no pool, so choosing it leaves the held link standing.
const FORM_ONLY = "__form__";
const NEW_FIELD = "__new-field__";
const FULL_BLEED = "-mx-1";

const FORM_ONLY_OPTION: SelectComboboxOption = { label: "—", value: FORM_ONLY };

export type CreateFieldDoor =
  | ((
      type: CustomFieldType,
      onCreated: (field: CustomFieldDef) => void,
      seed?: CustomFieldDef,
    ) => void)
  | null;

// A null type (the field was deleted before the item kept its `fieldType`) offers every field.
function linkableFields(
  fields: readonly CustomFieldDef[],
  type: CustomFieldDef["type"] | null,
): CustomFieldDef[] {
  const kind = type ? questionKindForFieldType(type) : null;
  return [...fields]
    .filter((field) => kind === null || questionKindForFieldType(field.type) === kind)
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** The properties a question of this shape can link to, in the Summary's
 *  order. A null type offers every property, on `linkableFields`' terms. */
function linkableProperties(type: CustomFieldDef["type"] | null): FormProperty[] {
  const kind = type ? questionKindForFieldType(type) : null;
  return FORM_PROPERTIES.filter(
    (property) =>
      kind === null || questionKindForFieldType(FORM_PROPERTY_FIELD_TYPE[property]) === kind,
  );
}

type LinkTarget = { property: FormProperty } | { field: CustomFieldDef };

type LinkOption = SelectComboboxOption &
  LinkTarget & {
    icon: IconName;
    iconColor: ComponentProps<typeof Icon>["color"];
  };

function linkItem(
  item: FormFieldItem,
  target: LinkTarget,
  from: CustomFieldDef | null,
): FormItem {
  return "property" in target
    ? linkItemToProperty(item, target.property, from)
    : linkItemToField(item, target.field, from);
}

export function useLinkOptions(form: Pick<Form, "items">, itemId: string | null) {
  const fields = useAllCustomFields();
  const { settings: sourceSettings } = useIssueSourceSettings();
  const sourceOff = sourceSettings.projectSourcePolicy === "off";
  const taken = useMemo(
    () =>
      new Set(
        form.items.flatMap((row) =>
          row.itemId === itemId
            ? []
            : row.kind === "customField"
              ? [row.customFieldId]
              : row.kind === "property"
                ? [propertyFieldId(row.property)]
                : [],
        ),
      ),
    [form.items, itemId],
  );
  return useCallback(
    (type: CustomFieldDef["type"] | null, linkedProperty: FormProperty | null = null) => {
      const properties = linkableProperties(type)
        // A link made before Source was turned off still reads as linked.
        .filter(
          (property) => !(sourceOff && property === "issueSource" && property !== linkedProperty),
        )
        .map(
          (property): LinkOption => ({
            icon: formPropertyIcon(property),
            iconColor: "muted",
            label: FORM_PROPERTY_LABEL[property],
            property,
            value: propertyFieldId(property),
          }),
        );
      const custom = linkableFields(fields, type).map((row): LinkOption => {
        const meta = getFieldTypeMeta(row.type);
        return {
          field: row,
          icon: meta.icon,
          iconColor: meta.color,
          label: row.name,
          value: row.customFieldId,
        };
      });
      return [...properties, ...custom].filter((option) => !taken.has(option.value));
    },
    [fields, sourceOff, taken],
  );
}

function useAnswerLink({
  item,
  form,
  customFieldsById,
  onRelink,
}: {
  item: FormFieldItem;
  form: Pick<Form, "items">;
  customFieldsById: ReadonlyMap<string, CustomFieldDef>;
  onRelink: (relink: ItemRelink) => void;
}) {
  const linkedField =
    item.kind === "customField" ? (customFieldsById.get(item.customFieldId) ?? null) : null;
  const linkedProperty = item.kind === "property" ? item.property : null;
  const field = resolveItemField(item, customFieldsById);
  // The live field is the authority on shape; `fieldType` answers only once that field is gone.
  const type =
    field?.type ?? (item.kind === "customField" ? (item.fieldType ?? null) : null);

  const optionsFor = useLinkOptions(form, item.itemId);
  const options = useMemo(
    () => optionsFor(type, linkedProperty),
    [linkedProperty, optionsFor, type],
  );

  // Each conversion reads the item as it stands when the relink lands, so the label rule sees the name it replaces.
  const linkTo = (target: LinkTarget) =>
    onRelink((current) => linkItem(current, target, resolveItemField(current, customFieldsById)));
  const unlink = () =>
    onRelink((current) =>
      current.kind === "customField" || current.kind === "property"
        ? unlinkItem(current, resolveItemField(current, customFieldsById))
        : current,
    );

  return {
    linkTo,
    linkedField,
    linkedValue:
      item.kind === "customField"
        ? item.customFieldId
        : linkedProperty
          ? propertyFieldId(linkedProperty)
          : FORM_ONLY,
    options,
    shape: type ? getFieldTypeMeta(type).label : null,
    type,
    unlink,
  };
}

export function AnswerLinkSettings({
  item,
  form,
  customFieldsById,
  onRelink,
  onCreateField,
  children,
}: {
  item: FormFieldItem;
  form: Pick<Form, "items" | "settings">;
  customFieldsById: ReadonlyMap<string, CustomFieldDef>;
  onRelink: (relink: ItemRelink) => void;
  onCreateField: CreateFieldDoor;
  children?: ReactNode;
}) {
  const { linkTo, linkedField, linkedValue, options, shape, type, unlink } =
    useAnswerLink({ customFieldsById, form, item, onRelink });
  // A form-only choice question's own choices give way to the link's, so
  // linking it asks first. The new-field door carries them, so it never asks.
  // A form-only choice question's own choices give way to the link's, so linking asks first.
  const ownChoices =
    item.kind === "question" &&
    (item.field.config?.options ?? []).some((opt) => (opt?.label ?? "") !== "");
  const [replacing, setReplacing] = useState<LinkOption | null>(null);
  const { onCreate } = fieldDoor(item, type, linkTo, onCreateField);
  const value =
    linkedValue === FORM_ONLY || options.some((option) => option.value === linkedValue)
      ? linkedValue
      : null;

  return (
    <Box direction="column" gap="lg">
      {children}
      <Field fill>
        {/* The info mark sits beside the label, not in it: a press inside a label would land on the dropdown it names. */}
        <Box align="center" direction="row" gap="xs">
          <FieldLabel>Links to</FieldLabel>
          <Tooltip>
            <TooltipTrigger aria-label="About Links to" render={<Box align="center" />}>
              <Icon color="muted" name="info" size="xs" />
            </TooltipTrigger>
            <TooltipPopup className="max-w-60">
              Copies the answer onto the Project on Submit. Left empty, it stays on the form.
            </TooltipPopup>
          </Tooltip>
        </Box>
        {item.kind === "customField" && !linkedField ? (
          <Text size="xs" variant="destructive">
            {shape ? `Linked field deleted. Choose another ${shape} field.` : "Linked field deleted. Choose another field."}
          </Text>
        ) : null}
        <SelectCombobox
          items={[FORM_ONLY_OPTION, ...options]}
          onValueChange={(next) => {
            if (next === null || next === value) return;
            if (next === NEW_FIELD) onCreate?.();
            else if (next === FORM_ONLY) unlink();
            else {
              const option = options.find((row) => row.value === next);
              if (!option) return;
              if (ownChoices) setReplacing(option);
              else linkTo(option);
            }
          }}
          value={value}
        >
          <SelectComboboxButton
            aria-label="Links to"
            clearLabel="Remove link"
            onClear={value !== FORM_ONLY ? unlink : undefined}
            placeholder="Choose a field…"
          />
          <SelectComboboxPopup search="Search fields">
            <FieldLinkOptions
              linked={value !== FORM_ONLY}
              onCreate={onCreate}
              options={options}
            />
          </SelectComboboxPopup>
        </SelectCombobox>
      </Field>
      <AlertDialog
        open={replacing !== null}
        onOpenChange={(open) => {
          if (!open) setReplacing(null);
        }}
      >
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>Replace choices?</AlertDialogTitle>
            <AlertDialogDescription>
              Linking replaces this question's choices with {replacing?.label}'s. Removing
              the link later won't bring them back.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogClose render={<Button variant="outline" />}>Cancel</AlertDialogClose>
            <Button
              onClick={() => {
                if (replacing) linkTo(replacing);
                setReplacing(null);
              }}
            >
              Link
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </Box>
  );
}

function fieldDoor(
  item: FormFieldItem,
  type: CustomFieldType | null,
  linkTo: (target: LinkTarget) => void,
  onCreateField: CreateFieldDoor,
) {
  const seed =
    item.kind === "question" || item.kind === "attachment"
      ? { ...item.field, description: item.description ?? null, name: item.label }
      : undefined;
  const onCreate =
    onCreateField && type && type !== "dateTime"
      ? () => onCreateField(type, (created) => linkTo({ field: created }), seed)
      : null;
  return { onCreate };
}

function FieldLinkOptions({
  options,
  onCreate,
  linked,
}: {
  options: readonly LinkOption[];
  onCreate: (() => void) | null;
  linked: boolean;
}) {
  const { hasQuery, matches } = useSelectQuery();
  const visible = options.filter((option) => matches(option.label));
  const row = (option: LinkOption) => (
    <SelectItem indicator="end" key={option.value} size="lg" value={option.value}>
      <Icon color={option.iconColor} name={option.icon} size="md" />
      <Text size={null} truncate>
        {option.label}
      </Text>
    </SelectItem>
  );
  const properties = visible.filter((option) => "property" in option);
  const custom = visible.filter((option) => "field" in option);
  return (
    <>
      {hasQuery ? visible.map(row) : null}
      {!hasQuery && linked ? (
        <SelectItem indicator="end" size="lg" value={FORM_ONLY}>
          <Icon color="muted" name="unlink" size="md" />
          <Text size={null}>Remove link</Text>
        </SelectItem>
      ) : null}
      {!hasQuery && linked && visible.length > 0 ? <SelectSeparator className={FULL_BLEED} /> : null}
      {!hasQuery && properties.length > 0 ? (
        <SelectGroup>
          <SelectGroupLabel>Properties</SelectGroupLabel>
          {properties.map(row)}
        </SelectGroup>
      ) : null}
      {!hasQuery && properties.length > 0 && custom.length > 0 ? <SelectSeparator className={FULL_BLEED} /> : null}
      {!hasQuery && custom.length > 0 ? (
        <SelectGroup>
          <SelectGroupLabel>Custom Fields</SelectGroupLabel>
          {custom.map(row)}
        </SelectGroup>
      ) : null}
      <SelectEmpty>No matching fields</SelectEmpty>
      {!hasQuery && !linked && visible.length === 0 ? (
        // The "—" item still names the trigger, so SelectEmpty never sees
        // an empty list; this stands in for it.
        <Box justify="center" padding="sm">
          <Text size="sm" variant="muted">
            No fields to link
          </Text>
        </Box>
      ) : null}
      {onCreate ? (
        <>
          {!hasQuery || visible.length > 0 ? <SelectSeparator className={FULL_BLEED} /> : null}
          <SelectItem indicator="end" size="lg" value={NEW_FIELD}>
            <Icon color="muted" name="plus" size="md" />
            <Text size={null} variant="muted">
              New Custom Field…
            </Text>
          </SelectItem>
        </>
      ) : null}
    </>
  );
}
